import FetchWrapper from '@/helpers/api'
import Cookie from '@/helpers/cookies'
import { getFirestore, doc, setDoc } from 'firebase/firestore'
import { getAuth } from "firebase/auth";

const FITBIT_BASE_URI = 'https://api.fitbit.com/'
const FITBIT_USER_URI = FITBIT_BASE_URI + '1.1/user/'
const FITBIT_OAUTH_URI = FITBIT_BASE_URI + 'oauth2/'

export class FitbitAuth {
  constructor() {
    this.api = new FitbitAuthAPI()
  }

  static setAccessToken(accessToken) {
    // TODO: Remove temporary save to database.
    const auth = getAuth()
    const user = auth.currentUser;
    const db = getFirestore()
    const usersRef = doc(db, 'users', user.uid)
    setDoc(usersRef, {fitbitAccessToken: accessToken}, {merge: true})
    Cookie.set('fitbit_access_token', accessToken)
  }

  static setRefreshToken(refreshToken) {
    // TODO: Remove temporary save to database.
    const auth = getAuth()
    const user = auth.currentUser;
    const db = getFirestore()
    const usersRef = doc(db, 'users', user.uid)
    setDoc(usersRef, {fitbitRefreshToken: refreshToken}, {merge: true})
    Cookie.set('fitbit_refresh_token', refreshToken)
  }

  static setUserId(userId) {
    const auth = getAuth()
    const user = auth.currentUser;
    const db = getFirestore()
    const usersRef = doc(db, 'users', user.uid)
    setDoc(usersRef, {fitbitUserId: userId}, {merge: true})
    Cookie.set('fitbit_user_id', userId)
  }

  static getAccessToken() {
    return Cookie.get('fitbit_access_token')
  }

  static getRefreshToken() {
    return Cookie.get('fitbit_refresh_token')
  }

  static getUserId() {
    // TODO: Get userId from Firestore users collection.
    // const auth = getAuth()
    // const user = auth.currentUser;
    // const db = getFirestore()
    // const usersRef = doc(db, 'users', user.uid)
    // getDoc(usersRef)
    return Cookie.get('fitbit_user_id')
  }

  static clearTokens() {
    Cookie.erase('fitbit_access_token')
    Cookie.erase('fitbit_refresh_token')
    Cookie.erase('fitbit_user_id')
  }

  saveAuthentication(data) {
    FitbitAuth.setAccessToken(data.access_token)
    FitbitAuth.setRefreshToken(data.refresh_token)
    FitbitAuth.setUserId(data.user_id)
  }

  async requestAccessToken(authCode, codeVerifier) {
    const response = await this.api.post('token', {
      'client_id': process.env.VUE_APP_FITBIT_CLIENT_ID,
      'grant_type': 'authorization_code',
      'code': authCode,
      'code_verifier': codeVerifier,
    })
    this.saveAuthentication(response)
    return response
  }

  // Returns the API response if successful, otherwise throws an error.
  async refreshAccessToken() {
    const token = FitbitAuth.getRefreshToken()
    if (!token) {
      return new Error('No refresh token.')
    }
    const response = await this.api.post('token', {
      'client_id': process.env.VUE_APP_FITBIT_CLIENT_ID,
      'grant_type': 'refresh_token',
      'refresh_token': token,
    }).catch((error) => {
      throw error
    })
    this.saveAuthentication(response)
    return response
  }

  async revokeTokens() {
    FitbitAuth.clearTokens()
    const response = await this.api.post('revoke', {
      'client_id': process.env.VUE_APP_FITBIT_CLIENT_ID,
      'token': FitbitAuth.getAccessToken(),
    })
    await this.api.post('revoke', {
      'client_id': process.env.VUE_APP_FITBIT_CLIENT_ID,
      'token': FitbitAuth.getRefreshToken(),
    })
    this.saveAuthentication(response)
    return response
  }

  // Returns true if the token (access_token or refresh_token) is accepted as
  // valid. When the token is invalid, this function attempts to refresh it.
  // If this fails, we return false.
  async introspectToken(token) {
    const accessToken = FitbitAuth.getAccessToken()
    if (!accessToken) {
      return false
    }
    const oldURI = this.api.setBaseURI(FITBIT_BASE_URI + '1.1/oauth2/')
    const oldToken = this.api.setAccessToken(accessToken)
    const response = await this.api.post('introspect', {
      'token': token
    }).catch((error) => {
      // If rate limited, re-throw the error so it can be handled.
      if (error.status === 429) {
        throw error
      }

      this.api.setBaseURI(oldURI)
      this.api.setAccessToken(oldToken)
      return this.refreshAccessToken()
        .then(() => {
          return true
        })
        .catch(() => {
          return false
        })
    })
    this.api.setBaseURI(oldURI)
    this.api.setAccessToken(oldToken)
    
    return response
  }

  async isConnected() {
    const accessToken = FitbitAuth.getAccessToken()
    const response = await this.introspectToken(accessToken)
    return response ? response.active : false
  }
}

export class FitbitAuthAPI extends FetchWrapper {
  constructor() {
    super(FITBIT_OAUTH_URI, FetchWrapper.CONTENT_TYPE_FORM_ENCODED)
  }

  getAccessToken() {
    return null
  }

  async onError() {
    return null
  }
}

export class FitbitUserAPI extends FetchWrapper {
  constructor() {
    super(FITBIT_USER_URI)

    const userId = this.getUserId()
    if (!userId) {
      throw new Error("No Fitbit User ID")
    }
    this.baseURI = this.baseURI + userId + '/';
    this.accessToken = this.getAccessToken()
    this.auth = new FitbitAuth()
  }

  getAccessToken() {
    return FitbitAuth.getAccessToken()
  }

  getRefreshToken() {
    return FitbitAuth.getRefreshToken()
  }

  getUserId() {
    return FitbitAuth.getUserId()
  }

  revokeTokens() {
    new FitbitAuth()
    return this.auth.revokeTokens()
  }

  async onError(error) {
    // If response is 401 Unauthorized, then refresh token.
    if (error.status === 401) {
      return this.auth.refreshAccessToken()
    }
  }

  async getSleepTarget() {
    return this.get('sleep/goal.json')
      .then(data => {
        return data
      })
      .catch(() => {
        // Don't throw an error if one happens from this endpoint.
      })
  }

  async getSleepLogDates(dateFrom, dateTo) {
    return this.get('sleep/date/' + dateFrom + '/' + dateTo +'.json')
      .then(data => {
        return data.sleep
      })
  }

  async getSleepLogList(dateFrom) {
    const params = '?afterDate=' + dateFrom + '&sort=desc&limit=100&offset=0'
    return this.get('sleep/list.json' + params)
      .then(data => {
        // TODO: To handle pagination for requests that exceed 100 datapoints,
        // have a look at `data.pageination.next`.
        return data.sleep
      })
  }
}